// Single shared derivation of pricing_status for a FROZEN revision, used
// by every Frozen consumer (UnpricedItemsPage.jsx, excelExport.js) instead
// of each reimplementing the CASE independently. This mirrors
// estimation.boq_item_pricing_status's own live definition exactly:
//   is_excluded         -> 'excluded'
//   line_count = 0       -> 'unpriced'
//   any line's line_cost IS NULL -> 'incomplete'
//   selling_price = 0    -> 'zero_priced'
//   else                 -> 'priced'
// No financial calculation happens here -- line_cost itself is read
// verbatim from freeze_snapshot.full_indirect_data.rate_buildup_lines
// (captured at Freeze time from the live rate_buildup_line_cost View),
// so "line_cost IS NULL" is checked directly against the captured value,
// exactly like the live View's own rblc.line_cost IS NULL check -- not
// re-derived per method.
//
// KNOWN LIMITATION (unchanged, not addressed here): full_boq_data only
// contains non-excluded items (freeze_estimate_revision filters
// WHERE is_excluded = false). Excluded items are therefore absent from
// this derivation's input entirely, not classified 'excluded' -- this
// mirrors the existing Frozen BOQ/WBS/KPI pages' behavior for excluded
// items, not a new gap introduced here.
export function deriveFrozenPricingStatuses(boqData, rateBuildupLines) {
  const linesByItem = {}
  ;(rateBuildupLines || []).forEach((l) => {
    if (!linesByItem[l.boq_item_id]) linesByItem[l.boq_item_id] = []
    linesByItem[l.boq_item_id].push(l)
  })

  return (boqData || []).map((it) => {
    const itemLines = linesByItem[it.id] || []
    const lineCount = itemLines.length
    const nullLineCount = itemLines.filter((l) => l.line_cost == null).length

    let pricing_status
    if (it.is_excluded) pricing_status = 'excluded'
    else if (lineCount === 0) pricing_status = 'unpriced'
    else if (nullLineCount > 0) pricing_status = 'incomplete'
    else if (!(Number(it.selling_price) > 0)) pricing_status = 'zero_priced'
    else pricing_status = 'priced'

    return { boq_item_id: it.id, pricing_status, line_count: lineCount, null_line_count: nullLineCount }
  })
}

// True when the captured rate_buildup_lines cannot support a complete,
// non-misleading classification for this frozen revision (i.e. it was
// frozen before that data began being captured). Callers use this to
// show an explicit "not available" notice instead of guessing.
export function frozenPricingStatusUnavailable(boqData, rateBuildupLines) {
  return (rateBuildupLines || []).length === 0 && (boqData || []).some((it) => !it.is_excluded)
}
